import React, { useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

import iconMetaVendas from '~/assets/images/icon-meta-vendas.svg';
import iconTarget from '~/assets/images/icon-meta-target.svg';
import OpenChatBot from '~/components/layout/Chatbot';
import Header from '~/components/layout/Header';
import SideBar from '~/components/layout/Sidebar';
import Loading from '~/components/layout/Loading';
import goalsService from '~/services/goalsService';

import GoalSelector from './components/GoalSelector';
import InfoBanner from './components/InfoBanner';
import MetricsSection from './components/MetricsSection';
import EvolutionChart from './components/EvolutionChart';

import {
  Container,
  Content,
  HeaderContainer,
  PageContainer,
  MainContent,
  TopSection,
  TopRow,
  OrderCtaButton,
  LoadingOverlay,
} from './styles';
import {
  CenterLoadingContainer,
  CenterMessageContainer,
} from '~/components/layout/Sidebar/MobileVersion/styles';

// Todos os dados do painel 360° vêm da API de metas (goalsService / painel-metas).

export default function SalesDashboard() {
  const stores = useSelector(state => state.economicGroupReducer.stores);
  const classificationReducer = useSelector(
    state => state.classificationReducer,
  );
  const [selectedGoal, setSelectedGoal] = useState('toneladas');
  const [loading, setLoading] = useState(false);
  const [panelData, setPanelData] = useState(null);
  const [hasError, setHasError] = useState(false);
  const isWaitingApi = !hasError && !panelData;

  useEffect(() => {
    const fetchPanel = async () => {
      try {
        setHasError(false);
        const codeStore = localStorage.getItem('codeStore') || '';
        const siteCode = codeStore.includes('-') ? codeStore.split('-')[1] : '';

        if (!siteCode) {
          return;
        }

        setLoading(true);
        const res = await goalsService.getPainelMetas(siteCode);
        setPanelData(res.data);
      } catch (err) {
        console.warn('Erro ao carregar painel de metas', err);
        setPanelData(null);
        setHasError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchPanel();
  }, [stores]);

  const handleGoalChange = useCallback(
    newGoal => {
      if (newGoal === selectedGoal) return;
      setLoading(true);
      setTimeout(() => {
        // Troca de filtro só afeta o estado atual.
        // A persistência para F5 é feita no beforeunload.
        setSelectedGoal(newGoal);
        setLoading(false);
      }, 600);
    },
    [selectedGoal],
  );

  const monthlyCards =
    selectedGoal === 'p13'
      ? panelData && panelData.meta && panelData.venda && panelData.gapMensal
        ? [
            {
              icon: iconTarget,
              title: 'Meta de vendas',
              value: `${(panelData?.meta?.p13 ?? 0).toLocaleString(
                'pt-BR',
              )} P13`,
              description: (() => {
                const metaP13 = panelData?.meta?.p13;
                if (metaP13 === 0 || metaP13 == null) {
                  return 'Meta não cadastrada para cálculo.';
                }
                const gap = panelData?.gapMensal?.p13;
                if (gap == null) {
                  return 'Meta não cadastrada para cálculo.';
                }
                if (gap > 0) {
                  return `Faltam <strong>${gap.toLocaleString(
                    'pt-BR',
                  )} P13</strong> para você bater a meta mensal!`;
                }
                if (gap === 0) {
                  return 'Você atingiu a meta mensal!';
                }
                const excedente = Math.abs(gap);
                return `Você ultrapassou a meta em <strong>${excedente.toLocaleString(
                  'pt-BR',
                )} P13</strong>!`;
              })(),
            },
            {
              icon: iconMetaVendas,
              title: 'Vendas',
              value: `${(panelData?.venda?.p13 ?? 0).toLocaleString(
                'pt-BR',
              )} P13`,
              description: (() => {
                const comp = panelData?.comparativoAnoAnterior;
                const pct = comp?.mensalP13Percentual;
                if (
                  !comp ||
                  (typeof comp === 'object' &&
                    Object.keys(comp).length === 0) ||
                  pct == null
                ) {
                  return 'Sem histórico para comparação.';
                }
                return `<strong>${Math.abs(pct).toLocaleString('pt-BR', {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1,
                })}%</strong> ${
                  pct >= 0 ? 'acima' : 'abaixo'
                } do mesmo mês do ano anterior.`;
              })(),
            },
          ]
        : []
      : panelData && panelData.meta && panelData.venda && panelData.gapMensal
      ? [
          {
            icon: iconTarget,
            title: 'Meta de vendas',
            value: `${(panelData?.meta?.ton ?? 0).toLocaleString('pt-BR')} ton`,
            description: (() => {
              const metaTon = panelData?.meta?.ton;
              if (metaTon === 0 || metaTon == null) {
                return 'Meta não cadastrada para cálculo.';
              }
              const gap = panelData?.gapMensal?.ton;
              if (gap == null) {
                return 'Meta não cadastrada para cálculo.';
              }
              if (gap > 0) {
                return `Faltam <strong>${gap.toLocaleString(
                  'pt-BR',
                )} ton</strong> para você bater a meta mensal!`;
              }
              if (gap === 0) {
                return 'Você atingiu a meta mensal!';
              }
              const excedente = Math.abs(gap);
              return `Você ultrapassou a meta em <strong>${excedente.toLocaleString(
                'pt-BR',
              )} ton</strong>!`;
            })(),
          },
          {
            icon: iconMetaVendas,
            title: 'Vendas',
            value: `${(panelData?.venda?.ton ?? 0).toLocaleString(
              'pt-BR',
            )} ton`,
            description: (() => {
              const comp = panelData?.comparativoAnoAnterior;
              const pct = comp?.mensalTonPercentual;
              if (
                !comp ||
                (typeof comp === 'object' && Object.keys(comp).length === 0) ||
                pct == null
              ) {
                return 'Sem histórico para comparação.';
              }
              return `<strong>${Math.abs(pct).toLocaleString('pt-BR', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              })}%</strong> ${
                pct >= 0 ? 'acima' : 'abaixo'
              } do mesmo mês do ano anterior.`;
            })(),
          },
        ]
      : [];

  const dailyCards =
    selectedGoal === 'p13'
      ? panelData && panelData.diario
        ? [
            {
              icon: iconTarget,
              title: 'Meta de vendas',
              value: `${(panelData?.diario?.metaP13 ?? 0).toLocaleString(
                'pt-BR',
              )} P13`,
              description: (() => {
                const metaP13 = panelData?.diario?.metaP13;
                if (metaP13 === 0 || metaP13 == null) {
                  return 'Meta não cadastrada para cálculo.';
                }
                const gap = panelData?.diario?.gapP13;
                if (gap == null) {
                  return 'Meta não cadastrada para cálculo.';
                }
                if (gap > 0) {
                  return `Faltam <strong>${gap.toLocaleString(
                    'pt-BR',
                  )} P13</strong> para você bater a meta diária!`;
                }
                if (gap === 0) {
                  return 'Você atingiu a meta diária!';
                }
                const excedente = Math.abs(gap);
                return `Você ultrapassou a meta em <strong>${excedente.toLocaleString(
                  'pt-BR',
                )} P13</strong>!`;
              })(),
            },
            {
              icon: iconMetaVendas,
              title: 'Vendas',
              value: `${(panelData?.diario?.vendaP13 ?? 0).toLocaleString(
                'pt-BR',
              )} P13`,
              description: (() => {
                const comp = panelData?.comparativoAnoAnterior;
                const pct = comp?.diarioP13Percentual;
                if (
                  !comp ||
                  (typeof comp === 'object' &&
                    Object.keys(comp).length === 0) ||
                  pct == null
                ) {
                  return 'Sem histórico para comparação.';
                }
                return `<strong>${Math.abs(pct).toLocaleString('pt-BR', {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1,
                })}%</strong> ${
                  pct >= 0 ? 'acima' : 'abaixo'
                } em relação à meta diária do ano anterior.`;
              })(),
            },
          ]
        : []
      : panelData && panelData.diario
      ? [
          {
            icon: iconTarget,
            title: 'Meta de vendas',
            value: `${(panelData?.diario?.metaTon ?? 0).toLocaleString(
              'pt-BR',
            )} ton`,
            description: (() => {
              const metaTon = panelData?.diario?.metaTon;
              if (metaTon === 0 || metaTon == null) {
                return 'Meta não cadastrada para cálculo.';
              }
              const gap = panelData?.diario?.gapTon;
              if (gap == null) {
                return 'Meta não cadastrada para cálculo.';
              }
              if (gap > 0) {
                return `Faltam <strong>${gap.toLocaleString(
                  'pt-BR',
                )} ton</strong> para você bater a meta diária!`;
              }
              if (gap === 0) {
                return 'Você atingiu a meta diária!';
              }
              const excedente = Math.abs(gap);
              return `Você ultrapassou a meta em <strong>${excedente.toLocaleString(
                'pt-BR',
              )} ton</strong>!`;
            })(),
          },
          {
            icon: iconMetaVendas,
            title: 'Vendas',
            value: `${(panelData?.diario?.vendaTon ?? 0).toLocaleString(
              'pt-BR',
            )} ton`,
            description: (() => {
              const comp = panelData?.comparativoAnoAnterior;
              const pct = comp?.diarioTonPercentual;
              if (
                !comp ||
                (typeof comp === 'object' && Object.keys(comp).length === 0) ||
                pct == null
              ) {
                return 'Sem histórico para comparação.';
              }
              return `<strong>${Math.abs(pct).toLocaleString('pt-BR', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              })}%</strong> ${
                pct >= 0 ? 'acima' : 'abaixo'
              } em relação à meta diária do ano anterior.`;
            })(),
          },
        ]
      : [];

  const chartDataFromApi =
    panelData && Array.isArray(panelData.ultimos12Meses)
      ? panelData.ultimos12Meses.map(item => ({
          month: (item.mes || '').replace('-', '/'),
          value: selectedGoal === 'p13' ? item.p13 ?? 0 : item.ton ?? 0,
        }))
      : null;

  const chartData = chartDataFromApi || [];
  const chartTitle =
    selectedGoal === 'p13'
      ? 'Evolução em 12 meses (Vendas realizadas de P13 x Meses)'
      : 'Evolução em 12 meses (Toneladas x Meses)';

  return (
    <PageContainer>
      {loading && (
        <LoadingOverlay>
          <Loading margin="0" />
        </LoadingOverlay>
      )}
      <SideBar
        menuSelected={'Painel 360°'}
        hasPendenciOn={classificationReducer.pendenciesOn}
      >
        <Container>
          <Content>
            <HeaderContainer>
              <Header stores={stores} />
            </HeaderContainer>
            <MainContent>
              {hasError ? (
                <CenterMessageContainer>
                  Infelizmente não será possível acessar o painel de metas no
                  momento. Tente novamente mais tarde.
                </CenterMessageContainer>
              ) : isWaitingApi ? (
                <CenterLoadingContainer>
                  <Loading margin="0" />
                </CenterLoadingContainer>
              ) : (
                <>
                  <TopSection>
                    <TopRow>
                      <GoalSelector
                        selectedGoal={selectedGoal}
                        onGoalChange={handleGoalChange}
                      />
                      <OrderCtaButton to="/criar-pedido">
                        Faça o seu pedido de abastecimento agora
                      </OrderCtaButton>
                    </TopRow>
                    <InfoBanner />
                  </TopSection>

                  <MetricsSection
                    title="Mensal"
                    cards={monthlyCards}
                    dailyTitle="Diária"
                    dailyCards={dailyCards}
                  />
                  <EvolutionChart data={chartData} chartTitle={chartTitle} />
                </>
              )}
            </MainContent>
          </Content>
        </Container>
        <OpenChatBot />
      </SideBar>
    </PageContainer>
  );
}
